import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, Shield, CheckCircle2, Download, ArrowRight, FileText, Package } from 'lucide-react';
import BackButton from '../components/BackButton';

const DocumentOscopGE = () => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      
      {/* Bouton Retour */}
      <BackButton label="Retour" />

<Navigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-16 print:py-4 md:py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-4">
            <FileText className="h-5 w-5" />
            <span className="text-sm font-medium">DOCUMENT DE PRÉSENTATION</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            O'SCOP GE
          </h1>
          <p className="text-xl sm:text-2xl text-blue-100 mb-2 font-semibold">
            LE GROUPEMENT D'EMPLOYEURS COOPÉRATIF
          </p>
          <p className="text-lg text-white/90">
            Mutualiser les talents. Réussir ensemble.
          </p>
          
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center print:hidden">
            <Button 
              onClick={handlePrint}
              size="lg" 
              className="bg-white text-blue-600 hover:bg-blue-50 font-semibold"
            >
              <Download className="mr-2 h-5 w-5" />
              Télécharger / Imprimer
            </Button>
            <Button 
              asChild
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white/10 font-semibold"
            >
              <Link to="/oscop-ge">
                <ArrowRight className="mr-2 h-5 w-5" />
                Page complète
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Qui sommes-nous */}
          <Card className="mb-6 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
              <CardTitle className="text-2xl flex items-center gap-2">
                <Users className="h-6 w-6" />
                QUI SOMMES-NOUS ?
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                O'SCOP GE est le groupement d'employeurs coopératif d'Objectif SCOP Outremer. 
                Nous recrutons, accompagnons et formons les salariés que nous mettons à disposition des entreprises ultramarines. 
                Notre mission : <strong>sécuriser l'emploi local</strong> tout en <strong>soutenant les entreprises</strong> dans leur croissance.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Recrutement coopératif',
                  'Mise à disposition sécurisée de personnel',
                  'Formation continue (FORMA\'SCOP)',
                  'Appui RH et administratif',
                  'Accompagnement ESS'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Modèle Flexible */}
          <Card className="mb-6 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
              <CardTitle className="text-2xl flex items-center gap-2">
                <Package className="h-6 w-6" />
                UN MODÈLE FLEXIBLE : ADHÉSION + CRÉDITS
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Crédits de services */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">CRÉDITS DE SERVICES</h3>
                  <p className="text-gray-600 mb-4">1 crédit = 1 unité de service RH.</p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <span className="font-semibold text-gray-900">Junior</span>
                      <span className="text-blue-600 font-bold">18 crédits/h</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                      <span className="font-semibold text-gray-900">Standard</span>
                      <span className="text-green-600 font-bold">24 crédits/h</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <span className="font-semibold text-gray-900">Expert</span>
                      <span className="text-purple-600 font-bold">32 crédits/h</span>
                    </div>
                  </div>
                </div>

                {/* Packs disponibles */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">PACKS DISPONIBLES</h3>
                  <div className="space-y-2">
                    {[
                      { name: '100 crédits', bonus: 0 },
                      { name: '250 crédits', bonus: 10 },
                      { name: '500 crédits', bonus: 30 },
                      { name: '1000 crédits', bonus: 80 },
                      { name: '2000 crédits', bonus: 200 }
                    ].map((pack, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-900">{pack.name}</span>
                        {pack.bonus > 0 && (
                          <span className="text-green-600 font-semibold text-sm">
                            +{pack.bonus} bonus
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Avantages */}
          <Card className="mb-6 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
              <CardTitle className="text-2xl flex items-center gap-2">
                <Shield className="h-6 w-6" />
                LES AVANTAGES O'SCOP GE
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Réduction des coûts RH',
                  'Zéro risque administratif',
                  'Salariés formés et encadrés',
                  'Flexibilité totale',
                  'Service RH ESS externalisé',
                  'Impact économique territorial positif'
                ].map((avantage, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700 font-medium">{avantage}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Exemple */}
          <Card className="mb-6 shadow-lg border-2 border-yellow-400">
            <CardHeader className="bg-gradient-to-r from-yellow-400 to-orange-400">
              <CardTitle className="text-2xl">EXEMPLE</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700 font-medium">40h salarié standard</span>
                    <span className="text-2xl font-bold text-blue-600">960 crédits</span>
                  </div>
                  <div className="border-t-2 border-gray-300 pt-3">
                    <p className="text-gray-600 mb-2">Pack conseillé :</p>
                    <p className="text-xl font-bold text-green-600">Pack 1000 crédits</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <p className="text-gray-900 font-semibold text-lg">Budget maîtrisé ✓</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA Final */}
          <Card className="shadow-2xl border-4 border-blue-400">
            <CardContent className="p-8 text-center bg-gradient-to-br from-blue-50 to-indigo-50">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                REJOIGNEZ O'SCOP GE
              </h2>
              <p className="text-xl text-gray-700 mb-6">
                Le groupement coopératif qui sécurise l'emploi et soutient les entreprises ultramarines.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center print:hidden">
                <Button 
                  asChild
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 font-semibold"
                >
                  <Link to="/communityplace">
                    Découvrir nos offres
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button 
                  asChild
                  size="lg" 
                  variant="outline"
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold"
                >
                  <Link to="/oscop-ge">
                    En savoir plus
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
      
      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .print\\:hidden {
            display: none !important;
          }
          .print\\:py-8 {
            padding-top: 2rem !important;
            padding-bottom: 2rem !important;
          }
          body {
            background: white !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DocumentOscopGE;
